import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Save, X, List, GripVertical, AlertCircle, AlertTriangle, ArrowRight } from "lucide-react";
import {
  Template,
  PerguntaFormulario,
  BlocoPerguntas,
  etapasFunil,
  tiposTarefa,
  prioridades,
  ConclusaoAutomatica,
  LINHA_PRODUTO_TEMPLATE_LABELS,
  type LinhaProdutoTemplate,
} from '@/types/tarefas';
import { PerguntaFormularioEditor } from './PerguntaFormularioEditor';
import { ConclusoesAutomaticasEditor } from './ConclusoesAutomaticasEditor';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { useToast } from "@/hooks/use-toast";

interface TemplateFormProps {
  editingTemplate: Template | null;
  formData: Partial<Template>;
  onFormDataChange: (data: Partial<Template>) => void;
  onSave: () => void;
  onCancel: () => void;
  obterVinculoAnterior: (perguntaId: string, perguntas: PerguntaFormulario[]) => { pergunta: PerguntaFormulario; tipo: 'multipla_escolha' | 'discursiva'; respostas: string[] } | null;
  getPerguntaLabel: (perguntaId: string, perguntasBase?: PerguntaFormulario[]) => string;
}

export function TemplateForm({
  editingTemplate,
  formData,
  onFormDataChange,
  onSave,
  onCancel,
  obterVinculoAnterior,
  getPerguntaLabel
}: TemplateFormProps) {
  const { toast } = useToast();
  const [editingPergunta, setEditingPergunta] = useState<PerguntaFormulario | null>(null);
  const [showPerguntaForm, setShowPerguntaForm] = useState(false);
  const [blocosDisponiveis, setBlocosDisponiveis] = useState<BlocoPerguntas[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [perguntaParaDeletar, setPerguntaParaDeletar] = useState<string | null>(null);
  const [perguntasVinculadas, setPerguntasVinculadas] = useState<PerguntaFormulario[]>([]);

  useEffect(() => {
    // Buscar blocos disponíveis para exibir nomes
    const fetchBlocos = async () => {
      try {
        const response = await fetchWithAuth('/api/blocos-perguntas');
        if (response.ok) {
          const data: BlocoPerguntas[] = await response.json();
          setBlocosDisponiveis(data || []);
        }
      } catch (error) {
        console.error('Erro ao buscar blocos:', error);
      }
    };
    fetchBlocos();
  }, []);

  const perguntasOrdenadasPorOrdem = useMemo(() => (
    (formData.perguntas_formulario || [])
      .slice()
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
  ), [formData.perguntas_formulario]);

  // Perguntas marcadas como "fim" (finaliza_fluxo = true)
  const perguntasFim = useMemo(() => {
    return (formData.perguntas_formulario || [])
      .filter(p => p.finaliza_fluxo === true)
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  }, [formData.perguntas_formulario]);

  const getNumeroPerguntaVinculada = (perguntaId: string, perguntas: PerguntaFormulario[]) => {
    const vinculo = obterVinculoAnterior(perguntaId, perguntas);
    if (!vinculo) return null;

    const perguntasOrdenadas = perguntas.slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    const index = perguntasOrdenadas.findIndex((p) => p.id === vinculo.pergunta.id);
    return index >= 0 ? index + 1 : null;
  };

  const perguntasPendentes = useMemo(() => {
    const perguntas = formData.perguntas_formulario || [];
    return perguntas
      .filter((pergunta) => {
        if (pergunta.finaliza_fluxo) {
          return false;
        }

        if (pergunta.tipo_resposta === 'discursiva') {
          // Uma pergunta discursiva não está pendente se tiver proxima_pergunta_id OU proximo_bloco_id
          return !pergunta.proxima_pergunta_id && !pergunta.proximo_bloco_id;
        }

        if (pergunta.tipo_resposta === 'multipla_escolha') {
          const opcoesValidas = (pergunta.opcoes || []).map((opcao) => opcao.trim()).filter((opcao) => opcao !== '');
          if (opcoesValidas.length === 0) {
            return true;
          }
          const regras = Array.isArray(pergunta.regras_navegacao) ? pergunta.regras_navegacao : [];
          const respostasConfiguradas = new Set(
            regras
              .filter((regra) => regra.resposta && (regra.proxima_pergunta_id || regra.proximo_bloco_id))
              .map((regra) => (regra.resposta || '').trim())
              .filter((resposta) => resposta !== '')
          );
          const respostasSemDestino = opcoesValidas.filter((opcao) => !respostasConfiguradas.has(opcao));
          return respostasSemDestino.length > 0;
        }

        return false;
      })
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  }, [formData.perguntas_formulario]);

  const handleAddPergunta = () => {
    setEditingPergunta(null);
    setShowPerguntaForm(true);
  };

  const handleEditPergunta = (pergunta: PerguntaFormulario) => {
    // Criar uma cópia profunda da pergunta para evitar problemas de referência
    const perguntaEditada: PerguntaFormulario = {
      ...pergunta,
      opcoes: pergunta.opcoes ? [...pergunta.opcoes] : undefined,
      regras_navegacao: pergunta.regras_navegacao ? [...pergunta.regras_navegacao] : undefined
    };
    setEditingPergunta(perguntaEditada);
    setShowPerguntaForm(true);
  };

  const handleSavePergunta = (perguntaSalva: PerguntaFormulario, perguntaAnteriorId: string, respostasAnteriores: string[]) => {
    const perguntas = formData.perguntas_formulario || [];
    const estaEditando = Boolean(editingPergunta);
    const totalPerguntasAntes = perguntas.length;
    const estaCriandoPrimeiraPergunta = !estaEditando && totalPerguntasAntes === 0;
    const perguntaIdAtual = editingPergunta ? editingPergunta.id : perguntaSalva.id;
    const finalizaFluxo = perguntaSalva.finaliza_fluxo === true;

    // Remover vínculos existentes que apontam para esta pergunta
    let perguntasAtualizadas = perguntas.map((p) => {
      const regrasOriginais = Array.isArray(p.regras_navegacao) ? p.regras_navegacao : [];
      const regrasFiltradas = regrasOriginais.filter(
        (regra) => regra.proxima_pergunta_id !== perguntaIdAtual
      );
      const precisaAtualizarRegras = regrasFiltradas.length !== regrasOriginais.length;
      const precisaAtualizarNavegacao = p.proxima_pergunta_id === perguntaIdAtual;

      if (precisaAtualizarRegras || precisaAtualizarNavegacao) {
        return {
          ...p,
          regras_navegacao: p.regras_navegacao !== undefined ? regrasFiltradas : undefined,
          proxima_pergunta_id: precisaAtualizarNavegacao ? undefined : p.proxima_pergunta_id
        };
      }

      return p;
    });

    // Quando é bloco vinculado, não criamos uma nova pergunta, apenas atualizamos as regras da anterior
    // Se não é bloco ou se está editando, adicionamos/atualizamos a pergunta normalmente
    if (perguntaSalva.proximo_bloco_id && !estaEditando) {
      // Quando é bloco vinculado e não está editando, não adicionamos pergunta nova
      // As regras já foram aplicadas na pergunta anterior abaixo
    } else if (estaEditando) {
      // Quando está editando, atualizar a pergunta sendo editada
      // IMPORTANTE: Se está editando e o tipo de vínculo é bloco, não devemos manter proximo_bloco_id na pergunta
      // O proximo_bloco_id deve estar apenas nas regras da pergunta anterior
      if (perguntaSalva.proximo_bloco_id) {
        // Se está editando e mudou para bloco, remover proximo_bloco_id da pergunta atual
        // pois ele será aplicado nas regras da pergunta anterior
        const perguntaAtualizada = {
          ...perguntaSalva,
          proximo_bloco_id: undefined
        };
        perguntasAtualizadas = perguntasAtualizadas.map((p) =>
          p.id === perguntaIdAtual ? perguntaAtualizada : p
        );
      } else {
        // Se não é bloco, atualizar normalmente
        perguntasAtualizadas = perguntasAtualizadas.map((p) =>
          p.id === perguntaIdAtual ? perguntaSalva : p
        );
      }
    } else {
      perguntasAtualizadas = [...perguntasAtualizadas, perguntaSalva];
    }

    // IMPORTANTE: Sempre atualizar as regras da pergunta anterior quando há um vínculo
    // Isso funciona tanto para criação quanto para edição
    if (!estaCriandoPrimeiraPergunta && perguntaAnteriorId) {
      perguntasAtualizadas = perguntasAtualizadas.map((pergunta) => {
        if (pergunta.id !== perguntaAnteriorId) {
          return pergunta;
        }

        // Determinar se o vínculo é para pergunta ou bloco baseado na pergunta salva
        const temBlocoVinculado = !!perguntaSalva.proximo_bloco_id;
        const blocoIdVinculado = perguntaSalva.proximo_bloco_id;

        if (pergunta.tipo_resposta === 'multipla_escolha') {
          const respostasValidas = respostasAnteriores.filter((resposta) =>
            pergunta.opcoes?.includes(resposta)
          );
          const regrasExistentes = Array.isArray(pergunta.regras_navegacao)
            ? pergunta.regras_navegacao
            : [];
          
          // Remover regras existentes que apontam para esta pergunta ou para blocos nas respostas selecionadas
          // IMPORTANTE: Quando adicionamos uma nova pergunta ou bloco, devemos remover
          // todas as regras antigas para as respostas selecionadas para evitar duplicação
          const regrasFiltradas = regrasExistentes.filter(
            (regra) => {
              const respostaEstaSelecionada = respostasValidas.includes(regra.resposta);
              
              // Se a resposta está selecionada, precisamos substituir a regra
              if (respostaEstaSelecionada) {
                // Se estamos vinculando um bloco, remover todas as regras antigas (pergunta ou bloco) para substituir
                if (temBlocoVinculado && blocoIdVinculado) {
                  // Remover todas as regras antigas para esta resposta (tanto de pergunta quanto de bloco)
                  // Isso garante que quando editamos e mudamos para um bloco, as regras antigas sejam removidas
                  return false;
                } else {
                  // Se não estamos vinculando um bloco, estamos vinculando uma pergunta
                  // Remover regras de bloco antigas e regras de pergunta que apontam para outras perguntas
                  if (regra.proximo_bloco_id) {
                    return false; // Remover regras de bloco antigas
                  }
                  // IMPORTANTE: Quando estamos criando uma nova pergunta (não editando),
                  // não devemos manter regras que apontam para perguntaIdAtual porque essa pergunta
                  // ainda não existe na lista. Isso evita que uma pergunta se vincule a si mesma.
                  if (!estaEditando) {
                    // Ao criar nova pergunta, remover todas as regras antigas para as respostas selecionadas
                    return false;
                  }
                  // Ao editar, remover regras de pergunta antigas que apontam para outras perguntas
                  if (regra.proxima_pergunta_id && regra.proxima_pergunta_id !== perguntaIdAtual) {
                    return false;
                  }
                  // Manter se aponta para a pergunta atual (apenas ao editar)
                  return regra.proxima_pergunta_id === perguntaIdAtual;
                }
              }
              
              // Se a resposta não está selecionada, manter todas as regras (incluindo blocos)
              return true;
            }
          );
          
          // Verificar quais respostas já têm regras (para não duplicar)
          const respostasComRegras = new Set(regrasFiltradas.map(r => r.resposta));
          
          const novasRegras = [
            ...regrasFiltradas,
            ...respostasValidas
              .filter(resposta => !respostasComRegras.has(resposta)) // Não adicionar se já existe regra
              .map((resposta) => {
                if (temBlocoVinculado && blocoIdVinculado) {
                  return {
                    resposta,
                    proximo_bloco_id: blocoIdVinculado
                  };
                } else {
                  return {
                    resposta,
                    proxima_pergunta_id: perguntaIdAtual
                  };
                }
              })
          ];

          return {
            ...pergunta,
            regras_navegacao: novasRegras
          };
        } else if (pergunta.tipo_resposta === 'discursiva') {
          // Para perguntas discursivas, atualizar o vínculo diretamente
          if (temBlocoVinculado && blocoIdVinculado) {
            // Se é bloco, atualizar proximo_bloco_id
            return {
              ...pergunta,
              proximo_bloco_id: blocoIdVinculado,
              proxima_pergunta_id: undefined // Limpar proxima_pergunta_id se existir
            };
          } else {
            // Se é pergunta, atualizar proxima_pergunta_id
            return {
              ...pergunta,
              proxima_pergunta_id: perguntaIdAtual,
              proximo_bloco_id: undefined // Limpar proximo_bloco_id se existir
            };
          }
        }

        return pergunta;
      });
    }
    
    onFormDataChange({ ...formData, perguntas_formulario: perguntasAtualizadas });
    setShowPerguntaForm(false);
    setEditingPergunta(null);
  };

  // Função para encontrar todas as perguntas sucessoras (que vêm depois) da pergunta sendo deletada
  const encontrarPerguntasSucessoras = (perguntaId: string, perguntas: PerguntaFormulario[]): PerguntaFormulario[] => {
    const sucessoras = new Set<string>();
    const processadas = new Set<string>();
    const mapaPerguntas = new Map(perguntas.map(p => [p.id, p]));

    const encontrarRecursivo = (id: string) => {
      if (processadas.has(id)) return;
      processadas.add(id);

      const perguntaAtual = mapaPerguntas.get(id);
      if (!perguntaAtual) return;

      // Verificar vínculo direto via proxima_pergunta_id
      if (perguntaAtual.proxima_pergunta_id) {
        sucessoras.add(perguntaAtual.proxima_pergunta_id);
        encontrarRecursivo(perguntaAtual.proxima_pergunta_id);
      }

      // Verificar vínculos via regras_navegacao
      if (Array.isArray(perguntaAtual.regras_navegacao)) {
        perguntaAtual.regras_navegacao.forEach((regra) => {
          if (regra.proxima_pergunta_id) {
            sucessoras.add(regra.proxima_pergunta_id);
            encontrarRecursivo(regra.proxima_pergunta_id);
          }
        });
      }
    };

    encontrarRecursivo(perguntaId);
    return perguntas.filter((p) => sucessoras.has(p.id));
  };

  // Função para encontrar todas as perguntas que precisam ter vínculos transferidos
  // Retorna: { predecessoras: perguntas que apontam para a pergunta sendo deletada,
  //           todasSucessoras: todas as sucessoras que precisam ter vínculos transferidos }
  const encontrarPerguntasParaTransferencia = (perguntaId: string, perguntas: PerguntaFormulario[]) => {
    const sucessoras = encontrarPerguntasSucessoras(perguntaId, perguntas);

    // Perguntas predecessoras (que apontam para a pergunta sendo deletada)
    const predecessoras = perguntas.filter((p) => {
      if (p.proxima_pergunta_id === perguntaId) return true;
      if (Array.isArray(p.regras_navegacao)) {
        return p.regras_navegacao.some(r => r.proxima_pergunta_id === perguntaId);
      }
      return false;
    });

    // Para transferência manual, TODAS as perguntas sucessoras precisam ter seus vínculos transferidos
    // pois elas serão afetadas pela deleção da pergunta anterior na cadeia
    return {
      predecessoras,
      todasSucessoras: sucessoras
    };
  };

  const handleDeletePergunta = (perguntaId: string) => {
    const perguntas = formData.perguntas_formulario || [];
    const { todasSucessoras } = encontrarPerguntasParaTransferencia(perguntaId, perguntas);

    if (todasSucessoras.length > 0) {
      // Há perguntas sucessoras, mostrar modal de aviso
      setPerguntaParaDeletar(perguntaId);
      setPerguntasVinculadas(todasSucessoras);
      setShowDeleteDialog(true);
    } else {
      // Não há sucessoras, deletar diretamente
      if (!confirm('Tem certeza que deseja excluir esta pergunta?')) {
        return;
      }
      executarDelecao(perguntaId);
    }
  };

  const executarDelecao = (perguntaId: string) => {
    const perguntas = formData.perguntas_formulario || [];
    const { todasSucessoras, predecessoras } = encontrarPerguntasParaTransferencia(perguntaId, perguntas);
    // Deletar a pergunta e todas as sucessoras
    const idsParaDeletar = [perguntaId, ...todasSucessoras.map(p => p.id)];

    // Primeiro, limpar os vínculos das perguntas predecessoras que apontavam para a pergunta deletada
    let perguntasAtualizadas = perguntas.map((pergunta) => {
      // Se a pergunta está marcada para deleção, não precisa atualizar
      if (idsParaDeletar.includes(pergunta.id)) {
        return pergunta;
      }

      // Limpar proxima_pergunta_id se aponta para a pergunta sendo deletada
      if (pergunta.proxima_pergunta_id === perguntaId) {
        return {
          ...pergunta,
          proxima_pergunta_id: undefined
        };
      }

      // Limpar regras de navegação que apontam para a pergunta sendo deletada
      if (Array.isArray(pergunta.regras_navegacao) && pergunta.regras_navegacao.length > 0) {
        const regrasFiltradas = pergunta.regras_navegacao.filter(
          (regra) => regra.proxima_pergunta_id !== perguntaId
        );
        
        // Se houve alteração nas regras, atualizar a pergunta
        if (regrasFiltradas.length !== pergunta.regras_navegacao.length) {
          return {
            ...pergunta,
            regras_navegacao: regrasFiltradas.length > 0 ? regrasFiltradas : undefined
          };
        }
      }

      return pergunta;
    });

    // Remover todas as perguntas marcadas para deleção
    perguntasAtualizadas = perguntasAtualizadas.filter(p => !idsParaDeletar.includes(p.id));
    
    onFormDataChange({ ...formData, perguntas_formulario: perguntasAtualizadas });
  };

  const handleConfirmDelete = () => {
    if (!perguntaParaDeletar) return;

    // Confirmar deleção da pergunta e todas as sucessoras
    if (!confirm(`Tem certeza que deseja deletar a pergunta e todas as ${perguntasVinculadas.length} pergunta(s) sucessora(s)? Esta ação não pode ser desfeita.`)) {
      return;
    }

    executarDelecao(perguntaParaDeletar);
    setShowDeleteDialog(false);
    setPerguntaParaDeletar(null);
    setPerguntasVinculadas([]);
  };

  const handleCancelPergunta = () => {
    setShowPerguntaForm(false);
    setEditingPergunta(null);
  };

  return (
    <ScrollArea className="h-[500px] pr-4">
      <Card>
        <CardHeader>
          <CardTitle>
            {editingTemplate ? 'Editar Template' : 'Criar Novo Template'}
          </CardTitle>
          <CardDescription>
            Preencha os campos abaixo para {editingTemplate ? 'editar' : 'criar'} o template
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Campos do Formulário */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="titulo">Título *</Label>
              <Input
                id="titulo"
                value={formData.titulo}
                onChange={(e) => onFormDataChange({ ...formData, titulo: e.target.value })}
                placeholder="Ex: Primeiro Contato Imediato"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="objetivo">Objetivo</Label>
              <Textarea
                id="objetivo"
                value={formData.objetivo}
                onChange={(e) => onFormDataChange({ ...formData, objetivo: e.target.value })}
                placeholder="Descreva os objetivos a serem alcançados na tarefa"
                rows={3}
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => onFormDataChange({ ...formData, descricao: e.target.value })}
                placeholder="Breve descrição da tarefa"
                rows={2}
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="instrucoes">Instruções</Label>
              <Textarea
                id="instrucoes"
                value={formData.instrucoes}
                onChange={(e) => onFormDataChange({ ...formData, instrucoes: e.target.value })}
                placeholder="Instruções detalhadas para o vendedor"
                rows={4}
              />
            </div>

            <div>
              <Label htmlFor="etapa_funil">Etapa do Funil *</Label>
              <Select
                value={formData.etapa_funil}
                onValueChange={(value) => onFormDataChange({ ...formData, etapa_funil: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a etapa" />
                </SelectTrigger>
                <SelectContent>
                  {etapasFunil.map(etapa => (
                    <SelectItem key={etapa.value} value={etapa.value}>
                      {etapa.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="linha_produto">Linha de produto *</Label>
              <Select
                value={(formData.linha_produto as LinhaProdutoTemplate | undefined) || 'fotovoltaico'}
                onValueChange={(value: LinhaProdutoTemplate) =>
                  onFormDataChange({ ...formData, linha_produto: value })
                }
              >
                <SelectTrigger id="linha_produto">
                  <SelectValue placeholder="Selecione a linha" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(LINHA_PRODUTO_TEMPLATE_LABELS) as LinhaProdutoTemplate[]).map((k) => (
                    <SelectItem key={k} value={k}>
                      {LINHA_PRODUTO_TEMPLATE_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Templates de fotovoltaico não são aplicados a leads de carregadores veiculares, e vice-versa.
              </p>
            </div>

            <div>
              <Label htmlFor="tipo_tarefa">Tipo de Tarefa *</Label>
              <Select
                value={formData.tipo_tarefa}
                onValueChange={(value: any) => onFormDataChange({ ...formData, tipo_tarefa: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {tiposTarefa.map(tipo => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="prioridade">Prioridade *</Label>
              <Select
                value={formData.prioridade}
                onValueChange={(value: any) => onFormDataChange({ ...formData, prioridade: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a prioridade" />
                </SelectTrigger>
                <SelectContent>
                  {prioridades.map(prioridade => (
                    <SelectItem key={prioridade.value} value={prioridade.value}>
                      {prioridade.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="prazo_horas">Prazo (horas) *</Label>
              <Input
                id="prazo_horas"
                type="number"
                value={formData.prazo_horas ?? ''}
                onChange={(e) => onFormDataChange({ ...formData, prazo_horas: e.target.value ? parseInt(e.target.value) : undefined })}
                min={1}
                required
                placeholder="Ex: 24"
              />
            </div>

            <div>
              <Label htmlFor="ordem">Ordem *</Label>
              <Input
                id="ordem"
                type="number"
                value={formData.ordem ?? 1}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  onFormDataChange({ ...formData, ordem: value > 0 ? value : 1 });
                }}
                min={1}
                placeholder="1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Número inteiro maior que zero que define a ordem do template na etapa do funil
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="ativo"
                checked={formData.ativo}
                onCheckedChange={(checked) => onFormDataChange({ ...formData, ativo: checked })}
              />
              <Label htmlFor="ativo">Ativo</Label>
            </div>
          </div>

          {/* Seção de Perguntas do Formulário */}
          <div className="border-t pt-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Label className="text-lg font-semibold flex items-center gap-2">
                  <List className="h-5 w-5" />
                  Perguntas do Formulário
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Crie perguntas que devem ser respondidas durante a execução da tarefa
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddPergunta}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Nova Pergunta
              </Button>
            </div>

            {/* Lista de Perguntas */}
            {formData.perguntas_formulario && formData.perguntas_formulario.length > 0 ? (
              <div className="space-y-3 mb-4">
                {formData.perguntas_formulario
                  .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
                  .map((pergunta, index) => {
                    const numeroVinculado = getNumeroPerguntaVinculada(pergunta.id, formData.perguntas_formulario || []);
                    return (
                      <Card key={pergunta.id} className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-xs font-semibold min-w-[2rem] text-center">
                                {index + 1}
                              </Badge>
                              <GripVertical className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{pergunta.texto}</span>
                              <Badge variant="outline" className="text-xs">
                                {pergunta.tipo_resposta === 'multipla_escolha' ? 'Múltipla Escolha' : 'Discursiva'}
                              </Badge>
                              {numeroVinculado && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                                  Vínculo pergunta {numeroVinculado}
                                </Badge>
                              )}
                              {pergunta.finaliza_fluxo && (
                                <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-300">
                                  Fim
                                </Badge>
                              )}
                            </div>
                            {pergunta.tipo_resposta === 'multipla_escolha' && pergunta.opcoes && (
                              <div className="ml-6 mt-2">
                                <p className="text-xs text-muted-foreground mb-1">Opções:</p>
                                <ul className="list-disc list-inside text-sm text-muted-foreground">
                                  {pergunta.opcoes.map((opcao, idx) => (
                                    <li key={idx}>{opcao}</li>
                                  ))}
                                </ul>
                                {Array.isArray(pergunta.regras_navegacao) && pergunta.regras_navegacao.length > 0 && (() => {
                                  // Filtrar apenas regras cujas respostas correspondem às opções desta pergunta
                                  const regrasValidas = pergunta.regras_navegacao.filter(regra => {
                                    const resposta = regra.resposta?.trim();
                                    if (!resposta) return false;
                                    return (pergunta.opcoes || []).some(opcao => opcao.trim() === resposta);
                                  });
                                  
                                  return regrasValidas.length > 0 ? (
                                    <div className="mt-2">
                                      <p className="text-xs text-muted-foreground mb-1">Encaminhamentos:</p>
                                      <ul className="text-xs text-muted-foreground space-y-1">
                                        {regrasValidas.map((regra, regraIdx) => {
                                          const blocoVinculado = regra.proximo_bloco_id 
                                            ? blocosDisponiveis.find(b => b.id === regra.proximo_bloco_id)
                                            : null;
                                          return (
                                            <li key={`${pergunta.id}-regra-${regraIdx}`}>
                                              <span className="font-semibold text-foreground">{regra.resposta || `Opção ${regraIdx + 1}`}</span>
                                              <span className="mx-1 text-muted-foreground">→</span>
                                              <span>
                                                {regra.proxima_pergunta_id
                                                  ? getPerguntaLabel(regra.proxima_pergunta_id, formData.perguntas_formulario)
                                                  : regra.proximo_bloco_id && blocoVinculado
                                                  ? `${blocoVinculado.nome} (bloco)`
                                                  : 'Encerrar formulário'}
                                              </span>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                            )}
                            {/* Exibir encaminhamento para perguntas discursivas vinculadas a blocos */}
                            {pergunta.tipo_resposta === 'discursiva' && pergunta.proximo_bloco_id && (
                              <div className="ml-6 mt-2">
                                <div className="mt-2">
                                  <p className="text-xs text-muted-foreground mb-1">Encaminhamento:</p>
                                  <ul className="text-xs text-muted-foreground space-y-1">
                                    <li>
                                      <span className="font-semibold text-foreground">Pergunta discursiva</span>
                                      <span className="mx-1 text-muted-foreground">→</span>
                                      <span>
                                        {(() => {
                                          const blocoVinculado = blocosDisponiveis.find(b => b.id === pergunta.proximo_bloco_id);
                                          return blocoVinculado 
                                            ? `${blocoVinculado.nome} (bloco)`
                                            : 'Bloco';
                                        })()}
                                      </span>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditPergunta(pergunta)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeletePergunta(pergunta.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground border rounded-lg">
                <List className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nenhuma pergunta adicionada ainda</p>
                <p className="text-xs mt-1">Clique em "Adicionar Nova Pergunta" para começar</p>
              </div>
            )}

            {/* Formulário de Adicionar/Editar Pergunta */}
            {showPerguntaForm && (
              <PerguntaFormularioEditor
                editingPergunta={editingPergunta}
                todasPerguntas={formData.perguntas_formulario || []}
                onSave={handleSavePergunta}
                onCancel={handleCancelPergunta}
                obterVinculoAnterior={obterVinculoAnterior}
                getPerguntaLabel={getPerguntaLabel}
              />
            )}

            {perguntasPendentes.length > 0 && (
              <div className="mt-6 rounded-lg border border-dashed border-amber-300 bg-amber-50/60 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="space-y-2">
                    <p className="font-medium text-amber-900">Vínculos pendentes</p>
                    <p className="text-sm text-amber-800">
                      Crie um vínculo sucessor marque como fim para as perguntas abaixo.
                    </p>
                    <ul className="space-y-1 text-sm text-amber-900">
                      {perguntasPendentes.map((pergunta) => (
                        <li key={`pendente-${pergunta.id}`} className="flex gap-2">
                          <span>•</span>
                          <span>{getPerguntaLabel(pergunta.id, perguntasOrdenadasPorOrdem)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Seção de Conclusões Automáticas */}
          {formData.perguntas_formulario && formData.perguntas_formulario.length > 0 && (
            <div className="border-t pt-6 mt-6">
              <ConclusoesAutomaticasEditor
                conclusoes={formData.conclusoes_automaticas || []}
                perguntasFim={perguntasFim}
                todasPerguntas={formData.perguntas_formulario}
                getPerguntaLabel={getPerguntaLabel}
                onChange={(conclusoes) => onFormDataChange({ ...formData, conclusoes_automaticas: conclusoes })}
              />
            </div>
          )}

          {/* Botões */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button onClick={onSave}>
              <Save className="h-4 w-4 mr-2" />
              {editingTemplate ? 'Atualizar' : 'Criar'} Template
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Aviso de Deleção com Vínculos */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Atenção: Pergunta com Vínculos
            </DialogTitle>
            <DialogDescription>
              Esta pergunta possui {perguntasVinculadas.length} pergunta(s) sucessora(s) que também serão deletadas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Pergunta a ser deletada */}
            {perguntaParaDeletar && (
              <Card className="border-red-200 bg-red-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Trash2 className="h-4 w-4 text-red-600" />
                    Pergunta a ser deletada
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-medium">
                    {getPerguntaLabel(perguntaParaDeletar, formData.perguntas_formulario)}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Lista de perguntas sucessoras */}
            {perguntaParaDeletar && perguntasVinculadas.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    Perguntas sucessoras que também serão deletadas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-3">
                    Estas {perguntasVinculadas.length} pergunta(s) são alcançadas diretamente ou indiretamente a partir da pergunta sendo deletada e serão removidas automaticamente.
                  </p>
                  <div className="max-h-[200px] overflow-y-auto border rounded-md bg-white p-3">
                    <ul className="space-y-2">
                      {perguntasVinculadas.map((p) => (
                        <li key={p.id} className="text-xs flex items-center gap-2">
                          <ArrowRight className="h-3 w-3 text-amber-600 flex-shrink-0" />
                          <span>{getPerguntaLabel(p.id, formData.perguntas_formulario)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Aviso sobre transferência manual */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Importante:</strong> Se você não deseja deletar as perguntas sucessoras, 
                você precisa <strong>cancelar esta ação</strong>, editar manualmente cada uma das perguntas listadas acima 
                para transferir seus vínculos para outras perguntas, e depois voltar aqui para deletar.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700">
              <Trash2 className="h-4 w-4 mr-2" />
              Confirmar Deleção
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ScrollArea>
  );
}

